import { FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";

import { OverrideRule, createDefaultRule } from "../types/override-rules";
import { OverrideRuleRow } from "./OverrideRuleRow";

interface OverrideRulesProps {
	rules: OverrideRule[];
	onChange: (rules: OverrideRule[]) => void;
	/**
	 * The explanation of the flow the rules feed, when it is not the find-person engine's (PREC-4788):
	 * the list's own scraper searches every profile in the database first, then AI broadens.
	 */
	infoText?: string;
}

export const OverrideRules: FunctionComponent<OverrideRulesProps> = ({
	rules,
	onChange,
	infoText,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.overrideRules.${key}`);

	const [isCollapsed, setIsCollapsed] = useState(rules.length === 0);

	const handleAddRule = useCallback(() => {
		const nextId = rules.length > 0 ? Math.max(...rules.map((r) => r.id)) + 1 : 1;

		onChange([...rules, createDefaultRule(nextId)]);
	}, [rules, onChange]);

	const handleDeleteRule = useCallback(
		(ruleId: number) => {
			onChange(rules.filter((r) => r.id !== ruleId));
		},
		[rules, onChange]
	);

	const handleUpdateRule = useCallback(
		(updatedRule: OverrideRule) => {
			onChange(rules.map((r) => (r.id === updatedRule.id ? updatedRule : r)));
		},
		[rules, onChange]
	);

	return (
		<div className="border border-border-light rounded-lg bg-white mt-6">
			{/* Header */}
			<div
				className="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
				onClick={() => setIsCollapsed(!isCollapsed)}
			>
				<div>
					<div className="flex items-center gap-2">
						<span className="text-sm font-semibold text-text">{ts("title")}</span>
						{isCollapsed && rules.length > 0 && (
							<span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-brand-white text-text-meta">
								{rules.length}{" "}
								{rules.length === 1
									? t("list.overrideRules.rule")
									: t("list.overrideRules.rulesCount")}
							</span>
						)}
					</div>
					<div className="text-xs text-text-meta">{ts("subtitle")}</div>
				</div>
				<svg
					className={`w-4 h-4 text-text-meta transition-transform ${isCollapsed ? "" : "rotate-180"}`}
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					viewBox="0 0 16 16"
				>
					<path d="M4 6l4 4 4-4" />
				</svg>
			</div>

			{/* Body */}
			{!isCollapsed && (
				<div className="px-4 pb-4">
					{rules.length > 0 && (
						<div className="border border-border-light rounded-lg divide-y divide-border-light">
							{rules.map((rule, index) => (
								<OverrideRuleRow
									key={rule.id}
									index={index}
									onChange={handleUpdateRule}
									onDelete={() => handleDeleteRule(rule.id)}
									rule={rule}
								/>
							))}
						</div>
					)}

					<div className="mt-3 w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.S}
							title={ts("addRule")}
							onClick={handleAddRule}
						/>
					</div>

					{/* Info text */}
					<div className="mt-4 pt-4 border-t border-border-light flex items-start gap-1.5 text-xs text-text-meta">
						<Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" icon={faInfoCircle} />
						<span>{infoText || ts("infoText")}</span>
					</div>
				</div>
			)}
		</div>
	);
};
